import {
  Box,
  Button,
  FormControl,
  InputAdornment,
  MenuItem,
  Typography,
  FormHelperText,
  SelectChangeEvent,
} from "@mui/material";
import { AppSelect, InputBase, InputLabel } from "components/common/Input";
import SettingsIcon from "@mui/icons-material/Settings";
import { Modal } from "components/common/Modal";
import { useFormik } from "formik";
import { AssetRetrievalReminderConfig } from "types/assetRetrieverReminderConfig";
import UploadClientLogo from "./UploadClientLogo";
import React, { useMemo } from "react";
import useAnalytics from "hooks/analytics/useAnalytics";

interface ReminderConfigurationModalProps {
  openReminder: boolean;
  setOpenReminder: React.Dispatch<React.SetStateAction<boolean>>;
  assetRetrievalReminderConfig: AssetRetrievalReminderConfig | undefined;
  createAssetReminderConfiguration: (assetRetrievalReminderConfig: AssetRetrievalReminderConfig) => Promise<void>;
  updateAssetReminderConfiguration: (assetRetrievalReminderConfig: AssetRetrievalReminderConfig) => Promise<void>;
  deleteAssetReminderConfiguration: () => void;
  setKey: (key: string) => void;
}

export default function ReminderConfigurationModal({
  openReminder,
  setOpenReminder,
  assetRetrievalReminderConfig,
  createAssetReminderConfiguration,
  updateAssetReminderConfiguration,
  deleteAssetReminderConfiguration,
  setKey,
}: ReminderConfigurationModalProps) {
  const { createAnalyticsEvent } = useAnalytics();

  const { getFieldProps, isSubmitting, setSubmitting, handleSubmit, values, setFieldValue } = useFormik({
    enableReinitialize: true,
    initialValues: assetRetrievalReminderConfig ?? {
      followupCadence: undefined as unknown as number,
      followupEscalationCount: undefined as unknown as number,
      followupEscalationEmail: "",
    },
    onSubmit: (values) => {
      assetRetrievalReminderConfig
        ? updateAssetReminderConfiguration(values).finally(() => {
            setOpenReminder(false);
            setSubmitting(false);
          })
        : createAssetReminderConfiguration(values).finally(() => {
            createAnalyticsEvent("asset_retrieval_configuration_created");
            setOpenReminder(false);
            setSubmitting(false);
          });
    },
  });

  const maxFollowups = useMemo(() => {
    if (!values.followupCadence) return 26;
    return Math.floor(80 / values.followupCadence);
  }, [values.followupCadence]);

  const handleCadenceChange = (event: SelectChangeEvent<number>) => {
    const newCadence = event.target.value as number;
    setFieldValue("followupCadence", newCadence);

    if (values.followupEscalationCount && values.followupEscalationCount > Math.floor(80 / newCadence)) {
      setFieldValue("followupEscalationCount", Math.floor(80 / newCadence));
    }
  };

  return (
    <Modal open={openReminder} setOpen={setOpenReminder}>
      <Box sx={{ px: 3, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}>
        <SettingsIcon
          color="primary"
          sx={{
            fontSize: "50px",
            mb: 3,
          }}
        />
        <Typography variant="h4" fontWeight="bold" mb={3}>
          Configure Reminders
        </Typography>
        <form onSubmit={handleSubmit}>
          <FormControl variant="standard" fullWidth>
            <InputLabel shrink htmlFor="employee-name" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              Send follow-up every:
            </InputLabel>
            <AppSelect
              {...getFieldProps("followupCadence")}
              onChange={handleCadenceChange}
              endAdornment={<InputAdornment position="end">days</InputAdornment>}
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: "15em",
                  },
                },
              }}
            >
              <MenuItem value={3}>3</MenuItem>
              <MenuItem value={4}>4</MenuItem>
              <MenuItem value={5}>5</MenuItem>
              <MenuItem value={6}>6</MenuItem>
              <MenuItem value={7}>7</MenuItem>
              <MenuItem value={8}>8</MenuItem>
              <MenuItem value={9}>9</MenuItem>
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={11}>11</MenuItem>
              <MenuItem value={12}>12</MenuItem>
              <MenuItem value={13}>13</MenuItem>
              <MenuItem value={14}>14</MenuItem>
            </AppSelect>
          </FormControl>

          <Box sx={{ display: "flex", gap: 2, alignItems: "center", mt: 4, mb: 4 }}>
            <div>After</div>
            <AppSelect
              {...getFieldProps("followupEscalationCount")}
              placeholder="# of follow-ups"
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: "15em",
                  },
                },
              }}
            >
              {Array.from({ length: maxFollowups }, (_, i) => i + 1).map((num) => (
                <MenuItem key={num} value={num}>
                  {num}
                </MenuItem>
              ))}
            </AppSelect>
            <div>send an escalation email to</div>
            <InputBase
              type="email"
              sx={{ width: "164px" }}
              placeholder="email address"
              {...getFieldProps("followupEscalationEmail")}
            />
          </Box>

          <FormHelperText sx={{ mt: -3, mb: 2, color: "text.secondary", fontSize: "0.75rem" }}>
            {maxFollowups} reminder emails max will be sent. Employee email addresses are automatically obfuscated for
            privacy purposes after 90 days.
          </FormHelperText>

          <FormControl variant="standard" fullWidth>
            <InputLabel shrink htmlFor="employee-name" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              Add your company logo to the reminders:
            </InputLabel>
            <UploadClientLogo
              fileUploaded={setKey}
              assetRetrievalReminderConfig={assetRetrievalReminderConfig}
              deleteAssetReminderConfiguration={deleteAssetReminderConfiguration}
            />
          </FormControl>

          <Box sx={{ display: "flex", gap: 2, justifyContent: "end", mt: 2, width: "100%" }}>
            <Button type="submit" variant="contained" color="primary" disableElevation disabled={isSubmitting}>
              save
            </Button>
          </Box>
        </form>
      </Box>
    </Modal>
  );
}
